// Dashboard.js
import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Fab,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Grid, // Import Grid for layout
  TextField, // Import TextField for text filters
  Button // Import Button
} from '@mui/material';
import { Add as AddIcon, FilterList as FilterListIcon } from '@mui/icons-material'; // Import FilterListIcon
import { useSearchParams } from 'react-router-dom';

import SideMenu from '../components/SideMenu';
import Jobs from './dashboard/Jobs';
// Import BackgroundChecks component
import JobDialog from '../components/JobDialog';
import { useUser } from '../context/UserContext'; // Import useUser hook
import useVehicleData from '../hooks/useVehicleData';
import { hasPermission, getUserOrganizationLevel } from '../utils/permissionStore'; // Import hasPermission and getUserOrganizationLevel from permissionStore
import { getDefaultDemoJob, createDemoJob } from '../services/jobService';
import { usePageTour } from '../tours/usePageTour';
// Import axios for API calls

// Define allowed roles for seeing filters
const rolesAllowedToFilter = {
  primary: ['OW', 'sOW', 'OWp', 'RM', 'SP'],
  secondary: ['admin', 'dispatcher', 'answeringService']
};

function Dashboard() {
  // State for job creation/editing dialog
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  
  // State for debug mode
  const [debugMode, setDebugMode] = useState(false);
  const [socketStatus, setSocketStatus] = useState({ connected: false, lastEvent: null });
  
  // State for Tabs
  const [tabValue, setTabValue] = useState(1);
  
  // State for notifications and loading
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [isLoading, setIsLoading] = useState(false);
  
  // Use custom hooks
  const { currentUser: userProfile, isLoading: userLoading, error: userError } = useUser(); // Use useUser hook, alias currentUser
  const { vehicleData } = useVehicleData();
  // First visit: the guided tour of this page (replayable from the ? button)
  usePageTour('dashboard', !!userProfile);
 
  // State for filter inputs (what user types)
  const [filterInputs, setFilterInputs] = useState({
    search: '',
    assignedVendorNumber: '',
    city: '',
    state: '',
    zip: '',
    service: '',
    customerName: '',
    customerPhone: '',
    poNumber: '',
    createdAt: ''
  });
  
  // State for applied filters (what gets sent to Jobs component)
  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    assignedVendorNumber: '',
    city: '',
    state: '',
    zip: '',
    service: '',
    customerName: '',
    customerPhone: '',
    poNumber: '',
    createdAt: ''
  });
  const [showFilters, setShowFilters] = useState(false); // State to toggle filter visibility
 
  // Determine if the current user should see filters
  const canUserFilter = userProfile && (
    rolesAllowedToFilter.primary.includes(userProfile.primaryRole) ||
    (userProfile.secondaryRoles && rolesAllowedToFilter.secondary.some(role => userProfile.secondaryRoles[role]))
  );
 
 
  // /dashboard?po=10000007 (from a bell notification) filters straight to that job
  const [searchParams] = useSearchParams();
  const poParam = searchParams.get('po') || '';
  useEffect(() => {
    if (!poParam) return;
    setFilterInputs((prev) => ({ ...prev, search: poParam, poNumber: poParam }));
    setAppliedFilters((prev) => ({ ...prev, search: poParam, poNumber: poParam }));
    setShowFilters(true);
  }, [poParam]);

  // Set tab based on user role
  useEffect(() => {
    // Use currentUser (aliased as userProfile) from context
    if (userProfile && userProfile.primaryRole === 'driver') {
      setTabValue(1); // Active Jobs tab
    }
  }, [userProfile]); // Keep dependency on userProfile (aliased currentUser)

  // Handler for tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
 
  // Handler for filter input changes (doesn't apply filters immediately)
  const handleFilterInputChange = (event) => {
    const { name, value } = event.target;
    setFilterInputs(prevInputs => ({
      ...prevInputs,
      [name]: value
    }));
  };
  
  // Handler for applying filters when search button is clicked
  const handleApplyFilters = () => {
    setAppliedFilters({ ...filterInputs });
  };
  
  // Handler for clearing all filters
  const handleClearFilters = () => {
    const emptyFilters = {
      search: '',
      assignedVendorNumber: '',
      city: '',
      state: '',
      zip: '',
      service: '',
      customerName: '',
      customerPhone: '',
      poNumber: '',
      createdAt: ''
    };
    setFilterInputs(emptyFilters);
    setAppliedFilters(emptyFilters);
  };
 
  // Toggle filter visibility
  const toggleFilterVisibility = () => {
    setShowFilters(prev => !prev);
  };

  // Handler for adding a new job
  const handleAddJob = () => {
    setIsEditing(false);
    setSelectedJob(null);
    setJobDialogOpen(true);
  };
  
  // Handler for editing job
  const handleEditJob = (job) => {
    setIsEditing(true);
    setSelectedJob(job);
    setJobDialogOpen(true);
  };

  // Handler for job dialog close
  const handleJobDialogClose = () => {
    setJobDialogOpen(false);
    setIsEditing(false);
    setSelectedJob(null);
  };

  // Job handlers are no longer needed as we're using web socket updates

  // Handler for creating a demo job
  const handleCreateDemoJob = useCallback(() => {
    setIsLoading(true);
    
    // Create a demo job object
    const demoJob = getDefaultDemoJob();
    
    // Make API call to create demo job
    createDemoJob(demoJob)
      .then((result) => {
        if (result.success) {
          setNotification({
            open: true,
            message: 'Demo job created successfully',
            severity: 'success'
          });
          
          // Job will be updated via websocket, no need to manually refresh
        } else {
          setNotification({
            open: true,
            message: result.message,
            severity: 'error'
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);
  
  // Handler for closing notifications
  const handleNotificationClose = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Side Menu */}
      <SideMenu />

      {/* Main Content */}
      <Container maxWidth={false} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box
          mt={4}
          mb={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h4">Dashboard</Typography>
          {/* Add New Job Button - Use permission system */}
          {hasPermission('canCreateJobs') && (
            <Fab color="primary" aria-label="add" onClick={handleAddJob} data-tour="dashboard-add-job">
              <AddIcon />
            </Fab>
          )}
        </Box>
 
        {/* Filter Section */}
        {canUserFilter && (
          <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: showFilters ? 2 : 0 }}>
              <Typography variant="h6">Filters</Typography>
              <Button
                startIcon={<FilterListIcon />}
                onClick={toggleFilterVisibility}
                aria-expanded={showFilters}
                data-tour="dashboard-filter-toggle"
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </Box>
            {showFilters && (
              <Grid container spacing={2} data-tour="dashboard-filters">
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search"
                    name="search"
                    placeholder="PO, customer, city, service, driver…"
                    value={filterInputs.search}
                    onChange={handleFilterInputChange}
                  />
                </Grid>
                {getUserOrganizationLevel(userProfile) === 1 && (
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Vendor Number"
                      name="assignedVendorNumber"
                      value={filterInputs.assignedVendorNumber}
                      onChange={handleFilterInputChange}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Creation Date"
                    name="createdAt"
                    type="date"
                    value={filterInputs.createdAt}
                    onChange={handleFilterInputChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="City"
                    name="city"
                    value={filterInputs.city}
                    onChange={handleFilterInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                   {/* TODO: Consider making State a dropdown */}
                   <TextField
                    fullWidth
                    size="small"
                    label="State"
                    name="state"
                    value={filterInputs.state}
                    onChange={handleFilterInputChange}
                  />
                </Grid>
                 <Grid item xs={12} sm={6} md={4} lg={2}>
                   <TextField
                    fullWidth
                    size="small"
                    label="Zip Code"
                    name="zip"
                    value={filterInputs.zip}
                    onChange={handleFilterInputChange}
                  />
                </Grid>
                 <Grid item xs={12} sm={6} md={4} lg={2}>
                   {/* TODO: Consider making Service a dropdown */}
                   <TextField
                    fullWidth
                    size="small"
                    label="Service"
                    name="service"
                    value={filterInputs.service}
                    onChange={handleFilterInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Customer Name"
                    name="customerName"
                    value={filterInputs.customerName}
                    onChange={handleFilterInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Customer Phone"
                    name="customerPhone"
                    value={filterInputs.customerPhone}
                    onChange={handleFilterInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="PO Number"
                    name="poNumber"
                    value={filterInputs.poNumber}
                    onChange={handleFilterInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleApplyFilters}
                      sx={{ minWidth: 'auto' }}
                    >
                      Search
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleClearFilters}
                      sx={{ minWidth: 'auto' }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Box>
        )}
 
        {/* Tabs */}
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="job categories"
          variant="scrollable"
          scrollButtons="auto"
          data-tour="dashboard-tabs"
        >
          <Tab label="Scheduled Jobs" data-tour="dashboard-tab-scheduled" />
          <Tab label="Active Jobs" data-tour="dashboard-tab-active" />
          <Tab label="Completed Jobs" data-tour="dashboard-tab-completed" />
          <Tab label="Canceled Jobs" data-tour="dashboard-tab-canceled" />
        </Tabs>

        {/* Debug Panel removed */}

        {/* Tab Panels */}
        <Box data-tour="dashboard-jobs">
        {userProfile && userProfile.primaryRole === 'driver' ? (
          // Driver view - show only jobs assigned to this driver
          <>
            {tabValue === 0 && (
              <Box mt={2}>
                {/* Scheduled Jobs */}
                <Typography variant="h5" gutterBottom>
                  Your Scheduled Jobs
                </Typography>
                <Jobs
                   jobCategory="scheduled"
                   onEditJob={handleEditJob}
                   onSocketStatusChange={setSocketStatus}
                   setNotification={setNotification} // Pass setNotification
                   filters={appliedFilters} // Pass applied filters
                 />
               </Box>
            )}
            {tabValue === 1 && (
              <Box mt={2}>
                {/* Active Jobs */}
                <Typography variant="h5" gutterBottom>
                  Your Active Jobs
                </Typography>
                <Jobs
                   jobCategory="inProgress"
                   onEditJob={handleEditJob}
                   onSocketStatusChange={setSocketStatus}
                   setNotification={setNotification} // Pass setNotification
                   filters={appliedFilters} // Pass applied filters
                 />
                 
                 <Typography variant="h5" mt={4} gutterBottom>
                  Jobs Awaiting Your Acceptance
                </Typography>
                <Jobs
                   jobCategory="pending"
                   onEditJob={handleEditJob}
                   onSocketStatusChange={setSocketStatus}
                   setNotification={setNotification} // Pass setNotification
                   filters={appliedFilters} // Pass applied filters
                 />
               </Box>
            )}
            {tabValue === 2 && (
              <Box mt={2}>
                {/* Completed Jobs */}
                <Typography variant="h5" gutterBottom>
                  Your Completed Jobs
                </Typography>
                <Jobs
                   jobCategory="completed"
                   onEditJob={handleEditJob}
                   onSocketStatusChange={setSocketStatus}
                   setNotification={setNotification} // Pass setNotification
                   filters={appliedFilters} // Pass applied filters
                 />
               </Box>
            )}
            {tabValue === 3 && (
              <Box mt={2}>
                {/* Canceled Jobs */}
                <Typography variant="h5" gutterBottom>
                  Your Canceled Jobs
                </Typography>
                <Jobs
                   jobCategory="canceled"
                   onEditJob={handleEditJob}
                   onSocketStatusChange={setSocketStatus}
                   setNotification={setNotification} // Pass setNotification
                   filters={appliedFilters} // Pass applied filters
                 />
               </Box>
            )}
          </>
        ) : (
          // Regular user view - show all jobs
          <>
            {tabValue === 0 && (
              <Box mt={2}>
                {/* Scheduled Jobs */}
                <Jobs
                  jobCategory="scheduled"
                   onCreateJob={handleAddJob}
                   onEditJob={handleEditJob}
                   onSocketStatusChange={setSocketStatus}
                   setNotification={setNotification} // Pass setNotification
                   filters={appliedFilters} // Pass applied filters
                 />
               </Box>
            )}
            {tabValue === 1 && (
              <Box mt={2}>
                {/* Active Jobs - Pending and In-Progress */}
                <Typography variant="h5" mt={4}>
                   Pending Jobs
                 </Typography>
                <Jobs
                  jobCategory="pending"
                  onCreateJob={handleAddJob}
                  onEditJob={handleEditJob}
                   onReceiveDemoJob={process.env.NODE_ENV !== 'production' ? handleCreateDemoJob : undefined}
                   onSocketStatusChange={setSocketStatus}
                   setNotification={setNotification} // Pass setNotification
                   filters={appliedFilters} // Pass applied filters
                 />
 
                  <Typography variant="h5" mt={4}>
                   In-Progress Jobs
                 </Typography>
                <Jobs
                   jobCategory="inProgress"
                   onEditJob={handleEditJob}
                   onSocketStatusChange={setSocketStatus}
                   setNotification={setNotification} // Pass setNotification
                   filters={appliedFilters} // Pass applied filters
                 />
               </Box>
            )}
            {tabValue === 2 && (
              <Box mt={2}>
                {/* Completed Jobs */}
                <Jobs
                   jobCategory="completed"
                   onEditJob={handleEditJob}
                   onSocketStatusChange={setSocketStatus}
                   setNotification={setNotification} // Pass setNotification
                   filters={appliedFilters} // Pass applied filters
                 />
               </Box>
            )}
            {tabValue === 3 && (
              <Box mt={2}>
                {/* Canceled Jobs */}
                <Jobs
                   jobCategory="canceled"
                   onEditJob={handleEditJob}
                   onSocketStatusChange={setSocketStatus}
                   setNotification={setNotification} // Pass setNotification
                   filters={appliedFilters} // Pass applied filters
                 />
               </Box>
            )}
          </>
        )}
        </Box>

        {/* Job Dialog */}
        <JobDialog
          open={jobDialogOpen}
          onClose={handleJobDialogClose}
          isEditing={isEditing}
          selectedJob={selectedJob}
          userProfile={userProfile} // Pass aliased currentUser
          // userCompanies={userCompanies} // userCompanies was part of useUserProfile, remove or get from currentUser if needed
          vehicleData={vehicleData}
          setNotification={setNotification}
        />
        
        {/* Notification Snackbar */}
        <Snackbar 
          open={notification.open} 
          autoHideDuration={6000} 
          onClose={handleNotificationClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleNotificationClose} 
            severity={notification.severity}
            variant="filled"
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}

export default Dashboard;
